import React from 'react'
import { UserService } from '@duck/api-definitions'
import { Badge, CopyableText } from '@duck/ui'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { usePersons } from '@/hooks/usePersons'
import { useOrganizations } from '@/storeHooks/useOrganizations'

const ONBOARDING_STATUS_LABELS: Record<UserService.PersonOnboardingStatusEnum, TranslationKey> = {
  [UserService.PersonOnboardingStatusEnum.Added]: 'users.person.statusAdded',
  [UserService.PersonOnboardingStatusEnum.Invited]: 'users.person.statusInvited',
  [UserService.PersonOnboardingStatusEnum.Joined]: 'users.person.statusJoined',
}

interface PersonDetailsSectionProps {
  user: UserService.User
}

export const PersonDetailsSection: React.FC<PersonDetailsSectionProps> = ({ user }) => {
  const { t } = useTar()
  const { persons } = usePersons(user.personId ? [user.personId] : undefined)
  const { idToNameMap } = useOrganizations()

  const person = persons.find((p) => p.id === user.personId)

  // "if exists": render nothing when there is no linked person (no personId, still
  // loading, or the person was deleted).
  if (!person) {
    return null
  }

  const emailDiffers =
    !!person.email &&
    person.email.trim().toLowerCase() !== (user.email ?? '').trim().toLowerCase()

  const managingOrgNames = person.managingOrganizationIds.map(
    (id) => idToNameMap.get(id) ?? id
  )
  const ongoingAssignmentsCount = person.ongoingAssignments?.length ?? 0
  const invitationsCount = person.invitationIds.length
  const statusLabel = ONBOARDING_STATUS_LABELS[person.onboardingStatus]

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
        {t('users.personDetails')}
      </h3>
      <div className="bg-card border border-border rounded-lg p-4 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Person ID */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('common.id')}
            </span>
            <CopyableText value={person.id} className="justify-start px-0 py-0">
              <span className="text-xs font-mono break-all text-foreground">
                {person.id}
              </span>
            </CopyableText>
          </div>

          {/* First name */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('common.firstName')}
            </span>
            <span className="text-foreground break-words">{person.firstName}</span>
          </div>

          {/* Last name */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('common.lastName')}
            </span>
            <span className="text-foreground break-words">{person.lastName}</span>
          </div>

          {/* Onboarding status */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('users.person.onboardingStatus')}
            </span>
            <Badge variant="outline" className="w-fit">
              {statusLabel ? t(statusLabel) : person.onboardingStatus}
            </Badge>
          </div>

          {/* Email (only when it differs from the user's email) */}
          {emailDiffers && (
            <div className="flex flex-col space-y-1">
              <span className="block text-xs font-medium text-muted-foreground">
                {t('users.person.emailMismatch')}
              </span>
              <CopyableText value={person.email} className="justify-start px-0 py-0">
                <span className="text-sm font-mono break-all text-foreground">
                  {person.email}
                </span>
              </CopyableText>
            </div>
          )}

          {/* Managing organizations */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('users.person.managingOrgs')} ({managingOrgNames.length})
            </span>
            <span className="text-foreground break-words">
              {managingOrgNames.length > 0 ? managingOrgNames.join(', ') : t('common.none')}
            </span>
          </div>

          {/* Ongoing assignments */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('users.person.ongoingAssignments')}
            </span>
            <span className="text-foreground">{ongoingAssignmentsCount}</span>
          </div>

          {/* Invitations */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('users.person.invitations')}
            </span>
            <span className="text-foreground">{invitationsCount}</span>
          </div>

          {/* Created */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('common.created')}
            </span>
            <span className="text-foreground">
              {person.createdAt ? (
                <RelativeDateTime isoDateString={person.createdAt} />
              ) : (
                t('common.na')
              )}
            </span>
          </div>

          {/* Updated */}
          <div className="flex flex-col space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t('users.person.updated')}
            </span>
            <span className="text-foreground">
              {person.updatedAt ? (
                <RelativeDateTime isoDateString={person.updatedAt} />
              ) : (
                t('common.na')
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
